import {
  Ban,
  Check,
  CircleHelp,
  Clock,
  Equal,
  Hand,
  LoaderCircle,
  MapPinOff,
  Pause,
  Pencil,
  Power,
  RefreshCw,
  SkipForward,
  TriangleAlert,
  X,
} from "lucide-react";

import type { Health, HealthIcon, Tone } from "@/lib/integrations/words";
import { outcomeOf } from "@/lib/integrations/words";

import styles from "./integrations.module.css";
import kit from "./kit.module.css";

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

export const TONE_PILL: Record<Tone, string> = {
  neutral: kit.pillNeutral,
  info: kit.pillAccent,
  good: kit.pillGood,
  warning: kit.pillWarn,
  danger: kit.pillBad,
};

export const TONE_FILL: Record<Tone, string> = {
  neutral: styles.fillNeutral,
  info: styles.fillInfo,
  good: styles.fillGood,
  warning: styles.fillWarning,
  danger: styles.fillDanger,
};

const SIZE = 13;

export function HealthGlyph({ icon, size = SIZE }: { icon: HealthIcon; size?: number }) {
  switch (icon) {
    case "pencil":
      return <Pencil size={size} aria-hidden />;
    case "spinner":
      return <LoaderCircle size={size} aria-hidden className={styles.spin} />;
    case "check":
      return <Check size={size} aria-hidden />;
    case "clock":
      return <Clock size={size} aria-hidden />;
    case "question":
      return <CircleHelp size={size} aria-hidden />;
    case "pause":
      return <Pause size={size} aria-hidden />;
    case "cross":
      return <X size={size} aria-hidden />;
    case "refresh":
      return <RefreshCw size={size} aria-hidden />;
    default:
      return <Power size={size} aria-hidden />;
  }
}

const BADGE: Record<Tone, string> = {
  neutral: "",
  info: styles.badgeInfo,
  good: styles.badgeGood,
  warning: styles.badgeWarning,
  danger: styles.badgeDanger,
};

/** Health as a block you read first: a large icon, the word, and one line of why. */
export function HealthBadge({ health, why }: { health: Health; why?: string }) {
  const said = why ?? health.why;
  return (
    <div className={cx(styles.badge, BADGE[health.tone])} role="status">
      <span className={styles.badgeIcon}>
        <HealthGlyph icon={health.icon} size={20} />
      </span>
      <span className={styles.badgeText}>
        <span className={styles.tileLabel}>Health</span>
        <span className={styles.badgeWord}>{health.word}</span>
        {said && (
          <span className={styles.badgeWhy} title={said}>
            {said}
          </span>
        )}
      </span>
    </div>
  );
}

/** Health word, icon and tone. The sentence saying why is its title. */
export function HealthPill({ health, count }: { health: Health; count?: number }) {
  return (
    <span className={cx(kit.pill, TONE_PILL[health.tone])} title={health.why}>
      <span className={styles.icon}>
        <HealthGlyph icon={health.icon} />
      </span>
      {health.word}
      {count !== undefined && count > 0 ? ` ${count}` : null}
    </span>
  );
}

function OutcomeGlyph({ outcome }: { outcome: string }) {
  switch (outcome) {
    case "accepted":
    case "ready":
      return <Check size={SIZE} aria-hidden />;
    case "unchanged":
      return <Equal size={SIZE} aria-hidden />;
    case "kept_by_hand":
      return <Hand size={SIZE} aria-hidden />;
    case "skipped":
      return <SkipForward size={SIZE} aria-hidden />;
    case "not_placed":
      return <MapPinOff size={SIZE} aria-hidden />;
    case "held":
      return <Pause size={SIZE} aria-hidden />;
    case "refused":
      return <Ban size={SIZE} aria-hidden />;
    default:
      return <TriangleAlert size={SIZE} aria-hidden />;
  }
}

/** A row's outcome: word, icon and tone. */
export function OutcomePill({ outcome, count }: { outcome: string; count?: number }) {
  const found = outcomeOf(outcome);
  return (
    <span className={cx(kit.pill, TONE_PILL[found.tone])} title={found.means}>
      <span className={styles.icon}>
        <OutcomeGlyph outcome={outcome} />
      </span>
      {found.word}
      {count !== undefined ? ` ${count.toLocaleString()}` : null}
    </span>
  );
}

/** A plain state with a tone, for words that are not health or an outcome (a run's status). */
export function TonePill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span className={cx(kit.pill, TONE_PILL[tone])} title={title}>
      {tone === "good" ? (
        <Check size={SIZE} aria-hidden />
      ) : tone === "danger" ? (
        <X size={SIZE} aria-hidden />
      ) : tone === "warning" ? (
        <TriangleAlert size={SIZE} aria-hidden />
      ) : tone === "info" ? (
        <LoaderCircle size={SIZE} aria-hidden />
      ) : (
        <span className={kit.dot} />
      )}
      {children}
    </span>
  );
}
